// Lead access helpers — single source of truth for "can this tradie see
// the customer's name and contact details?"
//
// A tradie is considered to have access when either:
//   (a) they have paid credits to unlock the lead (lead_unlocks row for
//       this business_id + lead_id), OR
//   (b) the lead is a direct link — the customer provided the tradie's
//       share link, so the lead row has directBusinessId === businessId
//       and no unlock is required.
//
// Everywhere else on the tradie side shows the customer as unnamed.

/**
 * @param {{ id: string, directBusinessId?: string|null }} lead
 * @param {string|null|undefined} businessId  — the current tradie's business id
 * @param {Array<string>|Set<string>} unlockedLeadIds  — lead ids this tradie has paid to unlock
 * @returns {boolean}
 */
export function isLeadUnlocked(lead, businessId, unlockedLeadIds) {
  if (!lead?.id) return false;
  const set =
    unlockedLeadIds instanceof Set
      ? unlockedLeadIds
      : new Set(unlockedLeadIds || []);
  if (set.has(lead.id)) return true;
  if (businessId && lead.directBusinessId === businessId) return true;
  // Quote-attached leads — if a quote from this business is on the lead,
  // the tradie clearly had access at some point (quotes_select_tradie RLS
  // only returns rows where business_id matches the caller's profile). This
  // catches winning_business_id leads and any historical quotes that
  // pre-date the lead_unlocks tracking table.
  if (lead.quote) return true;
  return false;
}

/** Placeholder shown to tradies who have not unlocked / are not direct-linked. */
export const LOCKED_CUSTOMER_LABEL = "Unnamed customer";

/**
 * Returns the customer display name for a lead from a tradie's perspective.
 * Locked leads always resolve to LOCKED_CUSTOMER_LABEL so the tradie UI
 * can't accidentally leak a contact name via an unguarded field.
 */
export function customerNameForTradie(lead, businessId, unlockedLeadIds) {
  if (!lead) return LOCKED_CUSTOMER_LABEL;
  return isLeadUnlocked(lead, businessId, unlockedLeadIds)
    ? lead.customer || lead.customer_name || LOCKED_CUSTOMER_LABEL
    : LOCKED_CUSTOMER_LABEL;
}
